import { Page } from "@eisox/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import { gatewayApi, valveApi } from "~/api";
import { AdvancedMaintenancePanel } from "~/features/advanced-maintenance";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { useBuildingContext } from "~/providers/building.provider";

const NAME = "AdvancedMaintenance";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/maintenance/advanced")({
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "maintenance", buildingId, preload);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { buildingId } = Route.useParams();
  const { building, gateways, valves, multisensors, modules, boilerRooms, heatingNetworks, permissions, date } =
    useBuildingContext(Route.id);

  const router = useRouter();
  const navigate = useNavigate();

  const handleClickOnFloor = (floorId: string) => {
    void navigate({ to: "/buildings/$buildingId/floors/$floorId", params: { buildingId, floorId } });
  };

  const handleRefresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries(gatewayApi.queries.building(buildingId)),
      queryClient.invalidateQueries(valveApi.queries.building(buildingId)),
    ]);
    await router.invalidate();
  };

  return (
    <Page.Content className="gap-5 p-2.5 md:p-10 flex h-full flex-col" data-testid={NAME}>
      <AdvancedMaintenancePanel
        building={building}
        gateways={gateways}
        valves={valves}
        multisensors={multisensors}
        modules={modules}
        boilerRooms={boilerRooms}
        heatingNetworks={heatingNetworks}
        permissions={permissions}
        date={date}
        onRefresh={handleRefresh}
        onClickOnFloor={handleClickOnFloor}
      />
    </Page.Content>
  );
}
